import { assert } from '@sparkdotfi/common-universal'

import { NativeAssetInfo } from '@/config/chain/types'
import { TokenWithBalance } from '@/domain/common/types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketInfo, Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { applyTransformers } from '@/utils/applyTransformers'

export interface GetDepositOptionsParams {
  reserve: Reserve
  marketInfo: MarketInfo
  walletInfo: MarketWalletInfo
  nativeAssetInfo: NativeAssetInfo
  isInSandbox: boolean
}

export function getRepayOptions({
  reserve,
  marketInfo,
  walletInfo,
  nativeAssetInfo,
  isInSandbox,
}: GetDepositOptionsParams): TokenWithBalance[] {
  const options = applyTransformers({ reserve, marketInfo, walletInfo, nativeAssetInfo, isInSandbox })([
    getNativeAssetRepayOptions,
    getUsdsRepayOptions,
    getDefaultRepayOptions,
  ])
  assert(options, `No deposit options found for token ${reserve.token.symbol}`)

  return options
}

function getNativeAssetRepayOptions({
  reserve,
  marketInfo,
  walletInfo,
  nativeAssetInfo,
}: GetDepositOptionsParams): TokenWithBalance[] | undefined {
  const { nativeAssetSymbol, wrappedNativeAssetSymbol } = nativeAssetInfo

  if (reserve.token.symbol !== nativeAssetSymbol && reserve.token.symbol !== wrappedNativeAssetSymbol) {
    return undefined
  }
  const native = marketInfo.findOneReserveBySymbol(nativeAssetSymbol)
  const wrapped = marketInfo.findOneReserveBySymbol(wrappedNativeAssetSymbol)

  return [
    { token: native.token, balance: walletInfo.findWalletBalanceForToken(native.token) },
    { token: wrapped.token, balance: walletInfo.findWalletBalanceForToken(wrapped.token) },
    { token: wrapped.aToken, balance: wrapped.aTokenBalance },
  ]
}

function getDefaultRepayOptions({ reserve, walletInfo }: GetDepositOptionsParams): TokenWithBalance[] {
  return [
    { token: reserve.token, balance: walletInfo.findWalletBalanceForToken(reserve.token) },
    { token: reserve.aToken, balance: reserve.aTokenBalance },
  ]
}

function getUsdsRepayOptions({
  reserve,
  marketInfo,
  walletInfo,
  isInSandbox,
}: GetDepositOptionsParams): TokenWithBalance[] | undefined {
  if (
    reserve.token.symbol !== TokenSymbol('USDS') ||
    (marketInfo.chainId !== 1 && !isInSandbox) ||
    import.meta.env.VITE_FEATURE_BORROW_USDC_FROM_USDS !== '1'
  ) {
    return undefined
  }

  const usdcReserve = marketInfo.findOneReserveBySymbol(TokenSymbol('USDC'))

  return [
    { token: reserve.token, balance: walletInfo.findWalletBalanceForToken(reserve.token) },
    { token: reserve.aToken, balance: reserve.aTokenBalance },
    { token: usdcReserve.token, balance: walletInfo.findWalletBalanceForToken(usdcReserve.token) },
  ]
}
